import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { useSearchParams } from "react-router-dom";
import { BarChart3, Users, MousePointerClick, ScrollText, RefreshCw, ShieldCheck, ExternalLink } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function AdminStats() {
  const [params, setParams] = useSearchParams();
  const [token, setToken] = useState(params.get("token") || "");
  const [tokenInput, setTokenInput] = useState(params.get("token") || "");
  const [summary, setSummary] = useState(null);
  const [recent, setRecent] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const load = async (t) => {
    if (!t) return;
    setLoading(true);
    setError("");
    try {
      const [s, r] = await Promise.all([
        axios.get(`${API}/analytics/summary`, { params: { token: t }, timeout: 15000 }),
        axios.get(`${API}/analytics/recent`, { params: { token: t, limit: 100 }, timeout: 15000 }),
      ]);
      setSummary(s.data);
      setRecent(r.data.items || []);
    } catch (e) {
      setError(e?.response?.data?.detail || "Failed to load analytics (bad token or server error).");
      setSummary(null);
      setRecent([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (token) load(token);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const submitToken = (e) => {
    e.preventDefault();
    setToken(tokenInput.trim());
    setParams({ token: tokenInput.trim() });
    load(tokenInput.trim());
  };

  if (!token || error) {
    return (
      <div className="min-h-screen bg-[#fdfaf5] grid place-items-center px-4">
        <form onSubmit={submitToken} className="w-full max-w-md bg-white border border-[#e2d5bb] rounded-2xl p-8 shadow-[0_30px_60px_-40px_rgba(0,0,0,0.25)]">
          <div className="flex items-center gap-2 text-[#7a1e2f]"><ShieldCheck size={18} /><span className="text-[11px] tracking-[0.24em] uppercase font-semibold">Admin · Stats</span></div>
          <h1 className="mt-3 font-serif-display text-3xl text-[#1a1a1a]">Take The Stage — analytics</h1>
          <p className="mt-2 text-sm text-[#1a1a1a]/60">Enter your admin token to view page views, click events and scroll depth.</p>
          <input
            type="password"
            value={tokenInput}
            onChange={(e) => setTokenInput(e.target.value)}
            placeholder="Admin token"
            className="mt-5 w-full rounded-xl border border-[#dccbb1] bg-[#fdfaf5] px-4 py-3 focus:border-[#7a1e2f] focus:ring-2 focus:ring-[#7a1e2f]/20 transition-colors"
          />
          {error && <p className="mt-3 text-sm text-[#8b1b31]">{error}</p>}
          <button type="submit" className="mt-5 w-full btn-primary rounded-full px-6 py-3.5 text-sm tracking-widest uppercase">Unlock</button>
        </form>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#fdfaf5]">
      <header className="border-b border-[#e9dfce] bg-white/70 backdrop-blur">
        <div className="max-w-7xl mx-auto px-4 lg:px-8 py-4 flex flex-wrap items-center justify-between gap-3">
          <div>
            <div className="flex items-center gap-2 text-[#7a1e2f]"><BarChart3 size={16} /><span className="text-[11px] tracking-[0.24em] uppercase font-semibold">Analytics</span></div>
            <h1 className="font-serif-display text-2xl md:text-3xl text-[#1a1a1a] leading-tight">Take The Stage — dashboard</h1>
          </div>
          <div className="flex items-center gap-3">
            <button onClick={() => load(token)} disabled={loading} className="btn-outline-dark rounded-full px-4 py-2 text-xs tracking-widest uppercase inline-flex items-center gap-2 disabled:opacity-60">
              <RefreshCw size={14} className={loading ? "animate-spin" : ""} /> Refresh
            </button>
            <a href="/" className="text-xs tracking-widest uppercase text-[#7a1e2f] inline-flex items-center gap-1.5">Landing <ExternalLink size={12} /></a>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 lg:px-8 py-8 space-y-8">
        {!summary ? (
          <div className="text-[#1a1a1a]/60">{loading ? "Loading…" : "No data yet."}</div>
        ) : (
          <>
            <Kpis summary={summary} />
            <div className="grid lg:grid-cols-3 gap-6">
              <DailyChart daily={summary.daily} />
              <ScrollCard scroll={summary.scroll} />
              <ApplicationsCard applications={summary.applications} />
            </div>
            <div className="grid lg:grid-cols-3 gap-6">
              <ListCard title="CTA clicks" icon={<MousePointerClick size={16} />} items={summary.cta_clicks} keyField="label" />
              <ListCard title="Devices (30d)" items={summary.devices} keyField="key" />
              <ListCard title="Browsers (30d)" items={summary.browsers} keyField="key" />
            </div>
            <ListCard title="Top referrers (30d)" items={summary.referrers} keyField="key" />
            <RecentTable recent={recent} />
          </>
        )}
      </main>
    </div>
  );
}

function Kpi({ label, value, sub }) {
  return (
    <div className="bg-white border border-[#e9dfce] rounded-2xl p-5">
      <div className="text-[11px] tracking-[0.2em] uppercase text-[#1a1a1a]/55">{label}</div>
      <div className="mt-2 font-serif-display text-3xl md:text-4xl text-[#1a1a1a]">{value}</div>
      {sub && <div className="mt-1 text-xs text-[#1a1a1a]/50">{sub}</div>}
    </div>
  );
}

function Kpis({ summary }) {
  const v = summary.views || {};
  const u = summary.unique_sessions || {};
  return (
    <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
      <Kpi label="Total visits" value={v.total ?? 0} sub={`${v.d1 ?? 0} in last 24h`} />
      <Kpi label="Visits last 7 days" value={v.d7 ?? 0} sub={`${v.d30 ?? 0} in last 30 days`} />
      <Kpi label="Unique visitors" value={u.total ?? 0} sub={`${u.d7 ?? 0} in last 7 days`} />
      <Kpi label="Applications" value={summary.applications?.total ?? 0} sub={`${summary.applications?.d7 ?? 0} in last 7 days`} />
    </div>
  );
}

function DailyChart({ daily }) {
  const max = useMemo(() => (daily || []).reduce((m, d) => Math.max(m, d.count), 0) || 1, [daily]);
  return (
    <div className="bg-white border border-[#e9dfce] rounded-2xl p-5 lg:col-span-2">
      <div className="flex items-center gap-2 text-[#7a1e2f]"><BarChart3 size={16} /><span className="text-[11px] tracking-[0.24em] uppercase font-semibold">Daily visits (14d)</span></div>
      <div className="mt-6 grid grid-cols-14 gap-1.5" style={{ gridTemplateColumns: `repeat(${(daily || []).length || 14}, minmax(0, 1fr))` }}>
        {(daily || []).map((d) => (
          <div key={d.date} className="flex flex-col items-center gap-1" title={`${d.date} · ${d.count} views`}>
            <div className="w-full h-32 flex items-end">
              <div className="w-full rounded-t bg-[#7a1e2f]" style={{ height: `${(d.count / max) * 100}%`, minHeight: d.count > 0 ? 4 : 0 }} />
            </div>
            <div className="text-[10px] text-[#1a1a1a]/50">{d.date.slice(5)}</div>
            <div className="text-[11px] font-medium text-[#1a1a1a]">{d.count}</div>
          </div>
        ))}
        {(!daily || daily.length === 0) && <div className="col-span-full text-sm text-[#1a1a1a]/50 py-4">No visits yet.</div>}
      </div>
    </div>
  );
}

function ScrollCard({ scroll }) {
  const buckets = scroll?.buckets || { 25: 0, 50: 0, 75: 0, 100: 0 };
  const sessions = scroll?.sessions || 0;
  const bar = (n) => (sessions ? Math.round((n / sessions) * 100) : 0);
  return (
    <div className="bg-white border border-[#e9dfce] rounded-2xl p-5">
      <div className="flex items-center gap-2 text-[#7a1e2f]"><ScrollText size={16} /><span className="text-[11px] tracking-[0.24em] uppercase font-semibold">Scroll depth</span></div>
      <div className="mt-3 text-xs text-[#1a1a1a]/60">Avg session max: <span className="text-[#1a1a1a] font-medium">{scroll?.avg_pct ?? 0}%</span> · {sessions} sessions</div>
      <div className="mt-4 space-y-2">
        {[25, 50, 75, 100].map((bkt) => (
          <div key={bkt}>
            <div className="flex justify-between text-[12px]"><span className="text-[#1a1a1a]/60">Reached {bkt}%</span><span className="text-[#1a1a1a] font-medium">{buckets[bkt] || 0} · {bar(buckets[bkt] || 0)}%</span></div>
            <div className="mt-1 h-2 rounded bg-[#f3ead9] overflow-hidden"><div className="h-full bg-[#7a1e2f]" style={{ width: `${bar(buckets[bkt] || 0)}%` }} /></div>
          </div>
        ))}
      </div>
    </div>
  );
}

function ApplicationsCard({ applications }) {
  return (
    <div className="bg-white border border-[#e9dfce] rounded-2xl p-5">
      <div className="flex items-center gap-2 text-[#7a1e2f]"><Users size={16} /><span className="text-[11px] tracking-[0.24em] uppercase font-semibold">Applications</span></div>
      <div className="mt-4 font-serif-display text-4xl text-[#1a1a1a]">{applications?.total ?? 0}</div>
      <div className="mt-1 text-sm text-[#1a1a1a]/60">{applications?.d7 ?? 0} in the last 7 days</div>
    </div>
  );
}

function ListCard({ title, icon, items, keyField }) {
  const max = (items || []).reduce((m, i) => Math.max(m, i.count), 0) || 1;
  return (
    <div className="bg-white border border-[#e9dfce] rounded-2xl p-5">
      <div className="flex items-center gap-2 text-[#7a1e2f]">{icon}<span className="text-[11px] tracking-[0.24em] uppercase font-semibold">{title}</span></div>
      <div className="mt-4 space-y-2">
        {(items || []).length === 0 && <div className="text-sm text-[#1a1a1a]/50">No data yet.</div>}
        {(items || []).map((it, i) => (
          <div key={i}>
            <div className="flex justify-between text-[13px]"><span className="text-[#1a1a1a]/75 truncate pr-3">{it[keyField] || "—"}</span><span className="text-[#1a1a1a] font-medium">{it.count}</span></div>
            <div className="mt-1 h-1.5 rounded bg-[#f3ead9] overflow-hidden"><div className="h-full bg-[#c9a961]" style={{ width: `${(it.count / max) * 100}%` }} /></div>
          </div>
        ))}
      </div>
    </div>
  );
}

function RecentTable({ recent }) {
  return (
    <div className="bg-white border border-[#e9dfce] rounded-2xl p-5">
      <div className="flex items-center gap-2 text-[#7a1e2f]"><BarChart3 size={16} /><span className="text-[11px] tracking-[0.24em] uppercase font-semibold">Recent activity (last {recent.length})</span></div>
      <div className="mt-3 overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="text-left text-[11px] tracking-[0.18em] uppercase text-[#1a1a1a]/50">
              <th className="py-2 pr-3">When</th>
              <th className="py-2 pr-3">Type</th>
              <th className="py-2 pr-3">Event / Path</th>
              <th className="py-2 pr-3">Meta</th>
              <th className="py-2 pr-3">Device</th>
              <th className="py-2 pr-3">Referrer</th>
            </tr>
          </thead>
          <tbody>
            {(recent || []).map((r) => (
              <tr key={r.id} className="border-t border-[#f3ead9]/70">
                <td className="py-2 pr-3 whitespace-nowrap text-[#1a1a1a]/70">{new Date(r.created_at).toLocaleString()}</td>
                <td className="py-2 pr-3"><span className={`px-2 py-0.5 rounded-full text-[11px] ${r.type === "pageview" ? "bg-[#f3ead9] text-[#7a1e2f]" : "bg-[#7a1e2f]/10 text-[#7a1e2f]"}`}>{r.type}</span></td>
                <td className="py-2 pr-3 text-[#1a1a1a]">{r.type === "pageview" ? r.path : r.event_name}</td>
                <td className="py-2 pr-3 text-[#1a1a1a]/60 font-mono text-xs">{r.event_meta && Object.keys(r.event_meta).length ? JSON.stringify(r.event_meta) : (r.type === "pageview" ? "—" : "")}</td>
                <td className="py-2 pr-3 text-[#1a1a1a]/70">{r.device} · {r.browser}</td>
                <td className="py-2 pr-3 text-[#1a1a1a]/60 truncate max-w-[240px]">{r.referrer || "(direct)"}</td>
              </tr>
            ))}
            {(!recent || recent.length === 0) && (
              <tr><td colSpan="6" className="py-4 text-[#1a1a1a]/50">No activity yet.</td></tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}
